// ══════════════════════════════════════════════════════════════════════════════
// storage.js — warstwa danych, model v2
//
// Kontrakt i los każdego eksportu z v1: docs/STORAGE-API-V2.md
// Schemat kolekcji:                     docs/DATA-MODEL-V2.md
// Kto co może przeczytać:               docs/ACCESS-MATRIX.md
//
// STRATEGIA bez zmian względem v1: localStorage jest podręcznym cache'em,
// Firestore źródłem prawdy. Odczyt do renderu idzie z cache'u (synchronicznie),
// zapis leci do obu miejsc, a odświeżenie z chmury dzieje się w tle.
//
// CO SIĘ ZMIENIŁO: dane nie leżą już w płaskiej kolekcji `kv` jako stringi
// JSON, tylko pod prawdziwymi ścieżkami z prawdziwymi polami. To nie jest
// kosmetyka — reguły Firestore operują na ścieżkach i polach, więc dopiero
// teraz da się je w ogóle napisać poprawnie.
//
// ── CZTERY ZASADY, KTÓRYCH NIE WOLNO ZŁAMAĆ ─────────────────────────────────
//
// 1. ZAPISUJEMY POLA, NIE DOKUMENTY. updateDoc z konkretnym patchem, nigdy
//    setDoc całością. Reguła update przypina ownerUid i teamId do wartości
//    z bazy; wysłanie całego dokumentu z nieaktualnym teamId (np. tuż po
//    joinTeam) kończy się permission-denied — po cichu, bo zapis jest
//    fire-and-forget.
//
// 2. ownerUid I teamId NIE WYCHODZĄ Z KLIENTA. Jedyny wyjątek to ownerUid
//    przy tworzeniu zawodnika. teamId ustawia wyłącznie CF joinTeam, bo musi
//    jednocześnie nadać claim memberTeams.
//
// 3. teamId ZAWSZE JAWNIE, null przy braku drużyny. Odwołanie do
//    nieistniejącego pola jest w regułach BŁĘDEM, nie null-em, a błąd
//    w warunku to odmowa.
//
// 4. KAŻDE ZAPYTANIE KOLEKCYJNE ZAWĘŻONE. Firestore sprawdza regułę dla
//    każdego zwracanego dokumentu i odrzuca CAŁE zapytanie, gdy choć jeden
//    nie przejdzie. Rodzic pyta po ownerUid, trener po teamId — także
//    o raporty.
// ══════════════════════════════════════════════════════════════════════════════

import { db, auth, functions } from "./firebase.js";
import {
  doc, collection, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { uid as genId } from "./utils/constants.js";

const me = () => auth.currentUser?.uid || null;

// ── Ścieżki ──────────────────────────────────────────────────────────────────
// Klucz w localStorage JEST ścieżką dokumentu — cache sam się dokumentuje
// i nie da się pomylić dokumentu z indeksem.
const P = {
  user:      uid => `users/${uid}`,
  private:   (uid, docId) => `users/${uid}/private/${docId}`,
  player:    id => `players/${id}`,
  team:      id => `teams/${id}`,
  materials: teamId => `teams/${teamId}/materials`,
  reports:   teamId => `teams/${teamId}/reports`,
  report:    (teamId, playerId, period) => `teams/${teamId}/reports/${playerId}_${period}`,
  pushSub:   uid => `push_subs/${uid}`,
};

// Indeksy list — wyłącznie lokalne, nie istnieją w Firestore.
const IDX = {
  myPlayers:   uid => `idx/players/owner/${uid}`,
  teamPlayers: teamId => `idx/players/team/${teamId}`,
  myTeams:     uid => `idx/teams/coach/${uid}`,
};

// ══════════════════════════════════════════════════════════════════════════════
//  CACHE
// ══════════════════════════════════════════════════════════════════════════════

function lsGet(key, fallback = null) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function lsSet(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (e) {
    console.warn("[FL] localStorage write fail:", key, e.message);
  }
}

function lsDel(key) {
  try { localStorage.removeItem(key); } catch {}
}

/**
 * Nakłada patch na obiekt tak, jak robi to Firestore updateDoc: klucz
 * z kropką ("english.progress") sięga w głąb mapy i podmienia TYLKO tę
 * gałąź. Cache i chmura muszą interpretować patch identycznie, inaczej
 * ekran pokazywałby co innego niż baza aż do przeładowania.
 */
function applyPatch(target, patch) {
  const out = { ...(target || {}) };
  for (const [key, value] of Object.entries(patch)) {
    if (!key.includes(".")) { out[key] = value; continue; }
    const parts = key.split(".");
    let cursor = out;
    for (let i = 0; i < parts.length - 1; i++) {
      cursor[parts[i]] = { ...(cursor[parts[i]] || {}) };
      cursor = cursor[parts[i]];
    }
    cursor[parts[parts.length - 1]] = value;
  }
  return out;
}

// ══════════════════════════════════════════════════════════════════════════════
//  DOSTĘP DO DOKUMENTÓW
// ══════════════════════════════════════════════════════════════════════════════

const refOf = path => doc(db, path);

// ── Memo sparsowanych dokumentów ────────────────────────────────────────────
// `readCached` woła render, i to w pętlach: buildTeamPlayers robi getPlayer na
// każdego zawodnika drużyny, PlayerDashboard dokłada getStreak i getTeam. Bez
// memo każde z tych wywołań parsowało CAŁY dokument od nowa. Zmierzone na
// dokumencie o pełnym kształcie (110 kB po roku aktywnego używania): 8,8 ms
// przy pięciu zawodnikach, 36 ms przy dwudziestu — na KAŻDY render, na
// desktopie. Po zmianie odpowiednio 0,002 i 0,015 ms.
//
// Kluczem unieważnienia jest surowy string z localStorage, nie licznik zapisów
// ani hook w lsSet. To jedyny wariant, który nie potrafi się rozjechać: zapis
// z drugiej karty, wipeLocalCache, ręczna zmiana w devtools czy jakakolwiek
// przyszła ścieżka omijająca lsSet dają inny string, więc dają miss. Licznik
// wymagałby pamiętania o nim w każdym nowym miejscu zapisu — czyli klasy błędu,
// która objawia się ekranem zamrożonym na starych danych.
//
// ZAŁOŻENIE, KTÓREGO NIE WOLNO ZŁAMAĆ: zwrócony obiekt jest WSPÓŁDZIELONY.
// Wcześniej każde wywołanie dostawało świeży wynik JSON.parse, więc mutacja
// w miejscu była nieszkodliwa i mogła się gdzieś zadomowić. Teraz zatrułaby
// cache wszystkim konsumentom. Audyt przy wprowadzaniu memo: wszystkie miejsca
// mutacji w src/ robią kopię przed zmianą (`{ ...progress }` w english.js,
// `{ ...child }` w playerActions.js, spread przed każdym `.sort()`),
// a applyPatch jest copy-on-write. Nowy kod musi to utrzymać — pilnuje tego
// test „readCached zwraca ten sam obiekt".
const parseMemo = new Map();
const PARSE_MEMO_MAX = 64;

/** Cache. Synchroniczne — tego używa render. */
function readCached(path, fallback = null) {
  let raw;
  try {
    raw = localStorage.getItem(path);
  } catch {
    return fallback;
  }
  if (!raw) { parseMemo.delete(path); return fallback; }

  const hit = parseMemo.get(path);
  if (hit && hit.raw === raw) {
    // Odświeżenie pozycji — Map trzyma kolejność wstawiania, więc delete+set
    // przesuwa wpis na koniec i chroni gorące dokumenty przed wyrzuceniem
    // przez rzadko czytane raporty.
    parseMemo.delete(path);
    parseMemo.set(path, hit);
    return hit.value;
  }

  let value;
  try {
    value = JSON.parse(raw);
  } catch {
    return fallback;
  }

  parseMemo.set(path, { raw, value });
  if (parseMemo.size > PARSE_MEMO_MAX) {
    parseMemo.delete(parseMemo.keys().next().value);
  }
  return value;
}

/** Firestore → cache. Zwraca null, gdy dokumentu nie ma albo odczyt padł. */
async function fetchDoc(path) {
  try {
    const snap = await getDoc(refOf(path));
    if (!snap.exists()) { lsDel(path); return null; }
    const data = snap.data();
    lsSet(path, data);
    return data;
  } catch (e) {
    console.warn("[FL] fetchDoc fail:", path, e.message);
    return null;
  }
}

/**
 * Zapis pól. Cache natychmiast, chmura w tle (fire-and-forget).
 *
 * updateDoc, nie setDoc(merge) — świadomie. Przy merge mapy zagnieżdżone
 * scalają się rekurencyjnie, więc USUNIĘCIE klucza z mapy nigdy nie dociera
 * do bazy. Dla `badges` czy `english` to różnica między „odznaka zniknęła"
 * a „odznaka wróciła po odświeżeniu".
 */
function patchDoc(path, patch) {
  lsSet(path, applyPatch(readCached(path), patch));
  updateDoc(refOf(path), patch)
    .catch(e => console.warn("[FL] patchDoc fail:", path, e.message));
}

/** Pełny zapis — tylko dla dokumentów BEZ pól sterujących dostępem. */
function putDoc(path, data) {
  lsSet(path, data);
  setDoc(refOf(path), data)
    .catch(e => console.warn("[FL] putDoc fail:", path, e.message));
}

/** Pełny zapis z potwierdzeniem — gdy kolejny krok zależy od zapisu. */
async function putDocAwaited(path, data) {
  lsSet(path, data);
  await setDoc(refOf(path), data);
  return true;
}

/**
 * Zapytanie kolekcyjne → cache dokumentów + cache indeksu id-ków.
 * `filters` to pary [pole, wartość]; równość, bo tylko takich używamy.
 */
async function fetchQuery(colPath, filters, idxKey) {
  try {
    const q = query(collection(db, colPath), ...filters.map(([f, v]) => where(f, "==", v)));
    const snap = await getDocs(q);
    const ids = [];
    snap.forEach(d => {
      lsSet(`${colPath}/${d.id}`, d.data());
      ids.push(d.id);
    });
    if (idxKey) lsSet(idxKey, ids);
    return ids;
  } catch (e) {
    // Najczęstsza przyczyna to zapytanie NIEZAWĘŻONE albo brak claima —
    // reguła odrzuca całość, nie pojedynczy dokument.
    console.warn("[FL] fetchQuery fail:", colPath, e.message);
    return idxKey ? lsGet(idxKey, []) : [];
  }
}

// ══════════════════════════════════════════════════════════════════════════════
//  WŁAŚCICIEL LOKALNEGO CACHE
// ══════════════════════════════════════════════════════════════════════════════
// localStorage żyje dłużej niż konto. Gdy ktoś usunie konto i założy nowe na
// ten sam adres (albo na współdzielonym komputerze zaloguje się druga osoba),
// przeglądarka nadal trzyma dane poprzedniego właściciela. Rozpoznanie po
// emailu tego nie wyłapie — email się powtarza, uid nigdy.

const OWNER_KEY = "fl_owner_uid";

const DEVICE_KEYS = new Set([
  OWNER_KEY, "fl_build", "fl_login_count", "fl_install_dismissed_at",
  "fl_push_login_count", "fl_push_dismissed_at",
]);
const isDeviceKey = k => DEVICE_KEYS.has(k) || k.startsWith("firebase");

export function getCacheOwner() {
  try { return localStorage.getItem(OWNER_KEY); } catch { return null; }
}

export function setCacheOwner(uid) {
  try { localStorage.setItem(OWNER_KEY, uid); } catch {}
}

/** Kasuje WYŁĄCZNIE localStorage. Firestore nietknięty — dane wrócą przy
 *  logowaniu prawowitego właściciela. */
export function wipeLocalCache() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && !isDeviceKey(k)) doomed.push(k);
    }
    doomed.forEach(lsDel);
    console.log("[FL] 🧹 Cache poprzedniego konta wyczyszczony:", doomed.length, "kluczy");
  } catch (e) {
    console.warn("[FL] wipeLocalCache fail:", e.message);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
//  KONTO — /users/{authUid}
// ══════════════════════════════════════════════════════════════════════════════

// `uid` doklejamy do zwracanego obiektu, bo jest identyfikatorem DOKUMENTU,
// a nie jego polem — w treści go nie ma i nie powinno być. Bez tego każdy
// konsument musiałby importować `auth` tylko po to, żeby poznać własny uid.
export function getMe() {
  const uid = me();
  if (!uid) return null;
  const d = readCached(P.user(uid));
  return d ? { ...d, uid } : null;
}

export async function loadMe() {
  const uid = me();
  if (!uid) return null;
  const d = await fetchDoc(P.user(uid));
  return d ? { ...d, uid } : null;
}

/** `role` celowo nie przechodzi — reguła i tak odrzuci zmianę, a cichy
 *  permission-denied jest gorszy niż brak próby. */
export function saveMe(patch) {
  const uid = me();
  if (!uid) return;
  const { role, ...safe } = patch;
  void role;
  if (Object.keys(safe).length) patchDoc(P.user(uid), safe);
}

export function subscribeToMe(callback) {
  const uid = me();
  return uid ? subscribeDoc(P.user(uid), callback) : () => {};
}

// ══════════════════════════════════════════════════════════════════════════════
//  DANE PRYWATNE — /users/{authUid}/private/{docId}
// ══════════════════════════════════════════════════════════════════════════════
// Koszty rodziny i refleksje mentalne. Trener nie ma tu wstępu — to jedyne
// miejsce, gdzie dostaje odmowę na dane związane z WŁASNYM zawodnikiem.
//
// Ścieżka biegnie przez authUid rodzica, nie przez playerId, i to jest cała
// ochrona: authUid pochodzi ze ŚCIEŻKI, nie z ładunku, więc nie ma jak zapisać
// niczego pod cudzym kontem. Wariant pod players/{id}/private opierał warunek
// tworzenia na polu ustawianym przez piszącego i miał realną dziurę.
//
// `playerId` w treści dokumentu nie służy do autoryzacji, tylko do sprzątania:
// CF deletePlayer kasuje po nim dane prywatne jednego dziecka, nie ruszając
// drugiego.

const privateId = (kind, playerId) => `${kind}_${playerId}`;

export function getPrivate(kind, playerId, fallback = null) {
  const uid = me();
  if (!uid) return fallback;
  const d = readCached(P.private(uid, privateId(kind, playerId)));
  return d ? d.value : fallback;
}

export async function loadPrivate(kind, playerId) {
  const uid = me();
  if (!uid) return null;
  const d = await fetchDoc(P.private(uid, privateId(kind, playerId)));
  return d ? d.value : null;
}

export function savePrivate(kind, playerId, value) {
  const uid = me();
  if (!uid) return;
  putDoc(P.private(uid, privateId(kind, playerId)), { kind, playerId, value });
}

// ── Koszty ───────────────────────────────────────────────────────────────────
export function getKoszty(playerId) { return getPrivate("finance", playerId, []) || []; }
export function saveKoszty(playerId, items) { savePrivate("finance", playerId, items); }

// ── Refleksje mentalne (moduł mental.js) ─────────────────────────────────────
// sessionMental — ocena 1–5 po sesji — NIE jest tutaj. Zostaje w dokumencie
// zawodnika, bo stanowi 25 z 100 punktów gotowości, którą trener widzi.
// Prywatne są refleksje, nie feedback treningowy.
export function getMentalHistory(playerId) { return getPrivate("mental", playerId, []) || []; }
export function saveMentalHistory(playerId, entries) { savePrivate("mental", playerId, entries); }

// ── Subskrypcja push ─────────────────────────────────────────────────────────
// Świeży odczyt z Firestore, nie z cache'u: przy rejestracji tokena z NOWEGO
// urządzenia trzeba scalić z devices[] zarejestrowanymi gdzie indziej, których
// lokalny cache nie zna.
export async function getPushSub() {
  const uid = me();
  return uid ? await fetchDoc(P.pushSub(uid)) : null;
}

export async function savePushSub(sub) {
  const uid = me();
  if (!uid) throw new Error("Brak aktywnej sesji");
  return await putDocAwaited(P.pushSub(uid), { ...sub, uid });
}

// ══════════════════════════════════════════════════════════════════════════════
//  ZAWODNICY — /players/{playerId}
// ══════════════════════════════════════════════════════════════════════════════

export function getPlayer(id) { return readCached(P.player(id)); }

export async function loadPlayer(id) { return await fetchDoc(P.player(id)); }

/**
 * Patch dokumentu zawodnika. ownerUid i teamId są odsiewane, bo reguła
 * przypina je do wartości z bazy — przepuszczenie ich to gwarantowane
 * permission-denied na każdym kolejnym zapisie.
 */
export function savePlayer(id, patch) {
  const { ownerUid, teamId, ...safe } = patch;
  void ownerUid; void teamId;
  if (Object.keys(safe).length) patchDoc(P.player(id), safe);
}

/** Jedyne miejsce, w którym ownerUid wychodzi z klienta. */
export async function createPlayer(profile) {
  const uid = me();
  if (!uid) throw new Error("Brak aktywnej sesji");
  const id = "p_" + genId();
  // Pola sterujące dostępem PO spreadzie — gdyby profile niosło własne
  // ownerUid, nadpisałoby zaufaną wartość.
  await putDocAwaited(P.player(id), {
    ...profile,
    ownerUid: uid,
    teamId: null,
    createdAt: Date.now(),
  });
  lsSet(IDX.myPlayers(uid), [...getMyPlayerIds(), id]);
  return id;
}

export function getMyPlayerIds() {
  const uid = me();
  return uid ? lsGet(IDX.myPlayers(uid), []) : [];
}

export async function listMyPlayers() {
  const uid = me();
  if (!uid) return [];
  return await fetchQuery("players", [["ownerUid", uid]], IDX.myPlayers(uid));
}

export function getTeamPlayerIds(teamId) {
  return teamId ? lsGet(IDX.teamPlayers(teamId), []) : [];
}

export async function listTeamPlayers(teamId) {
  if (!teamId) return [];
  return await fetchQuery("players", [["teamId", teamId]], IDX.teamPlayers(teamId));
}

export function subscribeToPlayer(id, callback) {
  return subscribeDoc(P.player(id), callback);
}

// ── Pola dokumentu zawodnika ─────────────────────────────────────────────────
// Seria, odznaki i postęp Footballingo były w v1 osobnymi dokumentami
// (streak_, badges_, eng_*). Teraz to pola, ale nazwy funkcji zostają —
// konsumenci (badges.js, englishBadges.js, EnglishDashboard) nie muszą
// o tym wiedzieć. Zapis idzie ścieżką z kropką, więc dotyka JEDNEJ gałęzi.

const PUSTA_SERIA = { currentStreak: 0, lastLogDate: null, bestStreak: 0 };

export function getStreak(id) { return getPlayer(id)?.streak || { ...PUSTA_SERIA }; }
export function saveStreak(id, data) { patchDoc(P.player(id), { streak: data }); }

export function getBadges(id) { return getPlayer(id)?.badges || []; }
export function saveBadges(id, arr) { patchDoc(P.player(id), { badges: arr }); }

export function getEnglishProgress(id) { return getPlayer(id)?.english?.progress || null; }
export function saveEnglishProgress(id, data) { patchDoc(P.player(id), { "english.progress": data }); }

export function getEnglishBadges(id) { return getPlayer(id)?.english?.badges || []; }
export function saveEnglishBadges(id, arr) { patchDoc(P.player(id), { "english.badges": arr }); }

// ══════════════════════════════════════════════════════════════════════════════
//  DRUŻYNY — /teams/{teamId}
// ══════════════════════════════════════════════════════════════════════════════
// teamId JEST kodem dołączenia. Sama znajomość kodu nie daje dostępu —
// odczyt wymaga claima, który nadaje CF po walidacji.

export function getTeam(teamId) { return teamId ? readCached(P.team(teamId)) : null; }

export async function loadTeam(teamId) { return teamId ? await fetchDoc(P.team(teamId)) : null; }

/** coachUid odsiewany — reguła przypina go przy update. */
export function saveTeam(teamId, patch) {
  const { coachUid, ...safe } = patch;
  void coachUid;
  if (Object.keys(safe).length) patchDoc(P.team(teamId), safe);
}

export function subscribeToTeam(teamId, callback) {
  return subscribeDoc(P.team(teamId), callback);
}

/**
 * Drużyny trenera pochodzą z CLAIMA, nie z zapytania — /teams nie wolno
 * listować (`allow list: if false`), a claim i tak jest jedynym, co daje
 * do nich dostęp.
 */
export async function loadMyTeamIds({ force = false } = {}) {
  const uid = me();
  if (!uid || !auth.currentUser) return [];
  try {
    const token = await auth.currentUser.getIdTokenResult(force);
    const ids = token.claims.coachTeams || [];
    lsSet(IDX.myTeams(uid), ids);
    return ids;
  } catch (e) {
    console.warn("[FL] loadMyTeamIds fail:", e.message);
    return lsGet(IDX.myTeams(uid), []);
  }
}

export function getMyTeamIds() {
  const uid = me();
  return uid ? lsGet(IDX.myTeams(uid), []) : [];
}

// ══════════════════════════════════════════════════════════════════════════════
//  MATERIAŁY — /teams/{teamId}/materials/{id}
// ══════════════════════════════════════════════════════════════════════════════
// W v1 wszystkie materiały drużyny siedziały w jednym dokumencie jako tablica,
// więc dodanie jednego przepisywało całość. Teraz to osobne dokumenty:
// równoległa edycja z dwóch urządzeń nie gubi cudzego wpisu.

const MAT_LIST = teamId => `${P.materials(teamId)}/__list`;

export function getMaterials(teamId) {
  return teamId ? lsGet(MAT_LIST(teamId), []) : [];
}

export async function listMaterials(teamId) {
  if (!teamId) return [];
  try {
    const snap = await getDocs(collection(db, P.materials(teamId)));
    const items = snap.docs.map(d => ({ ...d.data(), id: d.id }));
    lsSet(MAT_LIST(teamId), items);
    return items;
  } catch (e) {
    console.warn("[FL] listMaterials fail:", teamId, e.message);
    return getMaterials(teamId);
  }
}

/**
 * Dodaje materiał przez CF `submitMaterial` — tą samą drogą co raport.
 *
 * NIE setDoc z klienta, choć reguła nadal na to pozwala: powiadomienie o nowym
 * materiale musi wyjść z tej samej operacji, która tworzy dokument, a klient nie
 * ma czym wysłać pusha na INNE konta (rodziców drużyny). Dopóki zapis szedł
 * stąd, w całej ścieżce nie było żadnej CF i materiał pojawiał się u zawodnika
 * bez jakiegokolwiek sygnału.
 *
 * `id` i `createdAt` nadaje serwer i zwraca je w odpowiedzi, więc lokalny cache
 * dostaje dokładnie to, co wylądowało w bazie — bez drugiego odczytu.
 */
export async function saveMaterial(teamId, material) {
  const { id, createdAt } = await callCF("submitMaterial", {
    teamId,
    title: material.title,
    url:   material.url,
    desc:  material.desc,
    date:  material.date,
  });
  const data = { ...material, id, createdAt };
  lsSet(MAT_LIST(teamId), [...getMaterials(teamId).filter(m => m.id !== id), data]);
  return id;
}

export async function deleteMaterial(teamId, materialId) {
  await deleteDoc(refOf(`${P.materials(teamId)}/${materialId}`));
  lsSet(MAT_LIST(teamId), getMaterials(teamId).filter(m => m.id !== materialId));
}

// ══════════════════════════════════════════════════════════════════════════════
//  RAPORTY — /teams/{teamId}/reports/{playerId}_{period}
// ══════════════════════════════════════════════════════════════════════════════
// Odczyt regułami, ZAPIS wyłącznie przez CF. Reguła nie ma jak sprawdzić, czy
// ownerUid w raporcie należy do rodzica tego zawodnika, bez płatnego get() —
// bez tej weryfikacji trener podrzuciłby raport obcemu rodzicowi.

const REP_LIST = teamId => `${P.reports(teamId)}/__list`;

export function getReport(teamId, playerId, period) {
  return readCached(P.report(teamId, playerId, period));
}

export async function loadReport(teamId, playerId, period) {
  return await fetchDoc(P.report(teamId, playerId, period));
}

export function getReports(teamId) {
  return teamId ? lsGet(REP_LIST(teamId), []) : [];
}

/**
 * @param ownerUid  rodzic MUSI podać własny uid. Trener zostawia puste.
 *
 * Bez zawężenia zapytanie rodzica trafia na pierwszy cudzy raport i Firestore
 * odrzuca CAŁOŚĆ — nie odfiltrowuje, tylko odmawia. Trener zawężenia nie
 * potrzebuje, bo jego warunek (coachTeams) jest prawdziwy dla każdego raportu
 * w drużynie.
 */
export async function listReports(teamId, { ownerUid } = {}) {
  if (!teamId) return [];
  try {
    const col = collection(db, P.reports(teamId));
    const snap = await getDocs(ownerUid ? query(col, where("ownerUid", "==", ownerUid)) : col);
    const items = [];
    snap.forEach(d => {
      const data = d.data();
      lsSet(`${P.reports(teamId)}/${d.id}`, data);
      items.push({
        id: d.id,
        playerId: data.playerId,
        period: data.period,
        playerName: data.playerName || "",
        date: data.createdAt,
      });
    });
    lsSet(REP_LIST(teamId), items);
    return items;
  } catch (e) {
    console.warn("[FL] listReports fail:", teamId, e.message);
    return getReports(teamId);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
//  NASŁUCH
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Pomija PIERWSZE wywołanie (initial load) — callback tylko przy faktycznych
 * zmianach z zewnątrz. Dlatego sam listener nie wystarcza: zmiana powstała
 * przy zamkniętej aplikacji siedzi już w pierwszym snapshocie, czyli zostaje
 * pominięta. Świeże dane przynosi wtedy loadMyData() i zdarzenie
 * `fl-sync-complete`.
 */
function subscribeDoc(path, callback) {
  try {
    let isFirst = true;
    return onSnapshot(
      refOf(path),
      (snap) => {
        if (isFirst) { isFirst = false; return; }
        if (!snap.exists()) return;
        const data = snap.data();
        lsSet(path, data);
        // try/catch wokół samego callbacka: onSnapshot cicho wyłącza listener,
        // gdy handler rzuci — bez żadnego ostrzeżenia.
        try { callback(data); }
        catch (e) { console.warn("[FL] subscribeDoc: błąd callbacka dla", path, e.message); }
      },
      (err) => console.warn("[FL] subscribeDoc: listener error dla", path, err.message)
    );
  } catch (e) {
    console.warn("[FL] subscribeDoc setup error:", path, e.message);
    return () => {};
  }
}

// ══════════════════════════════════════════════════════════════════════════════
//  CLOUD FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

async function callCF(name, payload = {}) {
  try {
    const result = await httpsCallable(functions, name)(payload);
    return result.data;
  } catch (e) {
    console.warn("[FL] CF", name, "failed:", e.message);
    throw new Error(e.message || "Błąd serwera — sprawdź połączenie z internetem");
  }
}

/**
 * Czas serwera do korekty zegara klienta — docs/PAYMENTS-PLAN.md §K4 (przegląd
 * runda 3), patrz komentarz w src/utils/serverClock.js. Wołane raz przez
 * useAuth.js po (re)zalogowaniu; błąd (offline) jest zamierzenie ciche —
 * caller łapie go sam i zostaje przy offsecie 0.
 */
export async function callGetServerTime() { return await callCF("getServerTime"); }

/**
 * Claims wchodzą do tokena dopiero przy jego odświeżeniu, czyli do GODZINY.
 * Po każdej zmianie członkostwa wymuszamy odświeżenie — bez tego użytkownik
 * widzi „dołączyłeś do drużyny" i pustą drużynę.
 */
async function refreshClaims() {
  try { await auth.currentUser?.getIdToken(true); }
  catch (e) { console.warn("[FL] odświeżenie tokena nieudane:", e.message); }
}

// ── Rejestracja ──────────────────────────────────────────────────────────────
export async function callStartEmailVerification(role, payload = {}) {
  try {
    await httpsCallable(functions, "startEmailVerification")({ role, payload });
  } catch (e) {
    throw new Error(e.message || "Nie udało się wysłać kodu weryfikacyjnego");
  }
}

export async function callResendEmailVerification() {
  try {
    await httpsCallable(functions, "resendEmailVerification")({});
  } catch (e) {
    throw new Error(e.message || "Nie udało się wysłać kodu weryfikacyjnego");
  }
}

export async function callConfirmEmailVerification(code) {
  let data;
  try {
    const result = await httpsCallable(functions, "confirmEmailVerification")({ code });
    data = result.data;
  } catch (e) {
    throw new Error(e.message || "Nie udało się potwierdzić kodu");
  }
  // Rejestracja trenera nadaje claim coachTeams — bez odświeżenia nie
  // zobaczyłby własnej, przed chwilą założonej drużyny.
  await refreshClaims();
  await loadMe();
  if (data?.teamId) {
    lsSet(P.team(data.teamId), data.team);
    await loadMyTeamIds({ force: false });
  }
  return data;
}

// ── Drużyny ──────────────────────────────────────────────────────────────────
export async function callPreviewTeam(code) { return await callCF("previewTeam", { code }); }

export async function callJoinTeam(playerId, code) {
  const out = await callCF("joinTeam", { playerId, code });
  await refreshClaims();
  await loadPlayer(playerId);          // teamId ustawiła CF — cache musi to zobaczyć
  if (out.teamId) {
    lsSet(P.team(out.teamId), out.team);
    // Materiały i raporty dociągamy OD RAZU. Bez tego sekcja „Materiały od
    // trenera" jest pusta aż do następnego loadMyData — czyli do przeładowania
    // strony — mimo że użytkownik właśnie zobaczył „dołączono do drużyny".
    // Dopiero teraz mamy claim memberTeams, więc te odczyty w ogóle przejdą.
    const uid = me();
    await Promise.all([
      listMaterials(out.teamId),
      uid ? listReports(out.teamId, { ownerUid: uid }) : Promise.resolve([]),
    ]);
  }
  return out;
}

export async function callLeaveTeam(playerId) {
  const out = await callCF("leaveTeam", { playerId });
  await refreshClaims();
  await loadPlayer(playerId);
  // Claim zniknął, więc kolejny listMaterials i tak dostałby odmowę i wrócił
  // do cache'u. Czyścimy go sami, żeby dane opuszczonej drużyny nie zostały
  // na urządzeniu.
  const opuszczona = out.teamId || null;
  if (opuszczona) {
    lsDel(P.team(opuszczona));
    lsDel(MAT_LIST(opuszczona));
    lsDel(REP_LIST(opuszczona));
  }
  return out;
}

/**
 * Trener usuwa zawodnika z WŁASNEGO rosteru — docs/PAYMENTS-PLAN.md §K2
 * (przegląd runda 3). Zawodnik i jego dane zostają, tylko teamId wraca na
 * null po stronie CF; tutaj czyścimy index rosteru trenera z cache'u, żeby
 * zniknął z listy bez czekania na kolejny loadMyData.
 */
export async function callRemovePlayerFromTeam(teamId, playerId) {
  const out = await callCF("removePlayerFromTeam", { teamId, playerId });
  lsSet(IDX.teamPlayers(teamId), getTeamPlayerIds(teamId).filter(id => id !== playerId));
  return out;
}

export async function callCreateTeam(academy, birthYear) {
  const out = await callCF("createTeam", { academy, birthYear });
  await refreshClaims();
  if (out.teamId) {
    lsSet(P.team(out.teamId), out.team);
    await loadMyTeamIds({ force: false });
  }
  return out;
}

export async function callDeleteTeam(teamId) {
  const out = await callCF("deleteTeam", { teamId });
  await refreshClaims();
  lsDel(P.team(teamId));
  lsDel(IDX.teamPlayers(teamId));
  lsDel(MAT_LIST(teamId));
  lsDel(REP_LIST(teamId));
  await loadMyTeamIds({ force: false });
  return out;
}

// ── Zawodnicy ────────────────────────────────────────────────────────────────
export async function deletePlayer(playerId) {
  const out = await callCF("deletePlayer", { playerId });
  await refreshClaims();
  const uid = me();
  lsDel(P.player(playerId));
  if (uid) {
    lsSet(IDX.myPlayers(uid), getMyPlayerIds().filter(id => id !== playerId));
    lsDel(P.private(uid, privateId("finance", playerId)));
    lsDel(P.private(uid, privateId("mental", playerId)));
  }
  return out;
}

// ── Raporty ──────────────────────────────────────────────────────────────────
export async function callSubmitReport(teamId, playerId, period, data) {
  const out = await callCF("submitReport", { teamId, playerId, period, data });
  await loadReport(teamId, playerId, period);
  await listReports(teamId);
  return out;
}

export async function callRemoveReport(teamId, playerId, period) {
  const out = await callCF("removeReport", { teamId, playerId, period });
  lsDel(P.report(teamId, playerId, period));
  await listReports(teamId);
  return out;
}

// Świadomie BRAK wrappera na CF `sendTestPush`. Sama funkcja jest wdrożona
// i działa, ale przycisk w Profilu został usunięty — użytkownik nie ma po co
// wysyłać sobie powiadomień testowych. Do diagnozy kanału służy teraz albo
// `git revert` commita, który ten przycisk zdjął, albo wywołanie CF wprost
// z konsoli Firebase.

// ── Konto ────────────────────────────────────────────────────────────────────
/**
 * Kasuje konto W CAŁOŚCI, razem z kontem Firebase Auth — po stronie serwera.
 *
 * W v1 robił to klient i musiał układać operacje tak, żeby wszystkie zapisy
 * zdążyły przed user.delete(), bo to unieważnia token i reszta requestów leci
 * na brak uprawnień. Admin SDK nie podlega regułom, więc ten taniec znika.
 */
export async function callDeleteAccount() {
  const out = await callCF("deleteAccount", {});
  wipeLocalCache();
  return out;
}

// ── Płatności — Stripe (docs/PAYMENTS-PLAN.md §5.3A, §5.3c) ──────────────────
// Brak cache-sync po stronie klienta: obie funkcje tylko otwierają stronę
// Stripe, a prawdziwy stan player.pro wraca przez subscribeToPlayer po
// webhooku — tak samo jak raporty/materiały (CLAUDE.md, „Real-time listeners").
//
// returnUrl idzie z window.location.origin, bo serwer nie wie, pod jakim
// adresem akurat działa appka (dev, podgląd, produkcja) — CF waliduje, że to
// goły origin, zanim zbuduje z niego success_url/cancel_url/return_url.
// method: "card" (domyślnie, subskrypcja) albo "blik" (przedpłata jednorazowa
// — docs/PAYMENTS-PLAN.md §5.3B).
export async function callCreateCheckoutSession(playerId, plan, method = "card") {
  return await callCF("createCheckoutSession", { playerId, plan, method, returnUrl: window.location.origin });
}

export async function callCreateBillingPortalSession(playerId) {
  return await callCF("createBillingPortalSession", { playerId, returnUrl: window.location.origin });
}

// ══════════════════════════════════════════════════════════════════════════════
//  START APLIKACJI
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Następca syncAfterLogin. Bez argumentu `email` — email przestał cokolwiek
 * rozstrzygać, bo ścieżka /users/{uid} JEST tożsamością.
 *
 * Koszt dla trenera zależy od rozmiaru jego drużyny, a nie od liczby
 * użytkowników w systemie: prefix-scan player_* (900 odczytów przy 1000
 * userów) zastąpiło where("teamId","==",kod), czyli około 22.
 */
export async function loadMyData() {
  try {
    const uid = me();
    if (!uid) return null;

    const profile = await loadMe();
    if (!profile) {
      console.warn("[FL] loadMyData: brak dokumentu konta dla", uid);
      return null;
    }

    if (profile.role === "coach") await loadCoachData();
    else await loadParentData(uid);

    return profile;
  } catch (e) {
    console.warn("[FL] loadMyData failed:", e.message);
    return null;
  } finally {
    // Ekrany nie wiedzą, że cache się odświeżył — listenery pomijają pierwszy
    // snapshot, więc bez tego zdarzenia dane pojawiałyby się dopiero przy
    // KOLEJNYM przeładowaniu strony.
    try { window.dispatchEvent(new CustomEvent("fl-sync-complete")); } catch {}
  }
}

async function loadParentData(uid) {
  const playerIds = await listMyPlayers();

  await Promise.all(playerIds.map(async (id) => {
    const p = getPlayer(id);
    const jobs = [loadPrivate("finance", id), loadPrivate("mental", id)];
    if (p?.teamId) {
      jobs.push(
        loadTeam(p.teamId),
        listMaterials(p.teamId),
        listReports(p.teamId, { ownerUid: uid })
      );
    }
    await Promise.all(jobs);
  }));

  console.log("[FL] ✅ Sync rodzic — zawodników:", playerIds.length);
}

async function loadCoachData() {
  const teamIds = await loadMyTeamIds();

  await Promise.all(teamIds.map(teamId => Promise.all([
    loadTeam(teamId),
    listMaterials(teamId),
    listReports(teamId),
    listTeamPlayers(teamId),
  ])));

  console.log("[FL] ✅ Sync trener — drużyn:", teamIds.length);
}

// ══════════════════════════════════════════════════════════════════════════════
//  FIREBASE AUTH
// ══════════════════════════════════════════════════════════════════════════════

function authErrorPL(code) {
  const map = {
    "auth/email-already-in-use":   "Ten email jest już zajęty.",
    "auth/invalid-email":          "Nieprawidłowy adres email.",
    "auth/weak-password":          "Hasło za słabe — minimum 6 znaków.",
    "auth/user-not-found":         "Nie znaleziono konta z tym emailem.",
    "auth/wrong-password":         "Nieprawidłowe hasło.",
    "auth/invalid-credential":     "Nieprawidłowy email lub hasło.",
    "auth/too-many-requests":      "Zbyt wiele prób — spróbuj za chwilę.",
    "auth/network-request-failed": "Brak połączenia z internetem.",
  };
  return map[code] || "Błąd logowania. Spróbuj ponownie.";
}

export async function authRegister(email, password) {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return { uid: cred.user.uid };
  } catch (e) {
    throw new Error(authErrorPL(e.code));
  }
}

export async function authLogin(email, password) {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return { uid: cred.user.uid, email: cred.user.email };
  } catch (e) {
    throw new Error(authErrorPL(e.code));
  }
}

export async function authLogout() {
  try { await signOut(auth); }
  catch (e) { console.warn("[FL] logout error:", e.message); }
}

export async function authResetPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (e) {
    throw new Error(authErrorPL(e.code));
  }
}

/**
 * Kasuje WYŁĄCZNIE konto Auth. Używane tylko do porzucenia rejestracji
 * w trakcie weryfikacji kodem, gdy w Firestore nie ma jeszcze niczego.
 * Do kasowania prawdziwego konta służy callDeleteAccount().
 */
export async function authDeleteAccount() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Brak aktywnej sesji.");
    await user.delete();
    return true;
  } catch (e) {
    if (e.code === "auth/requires-recent-login") {
      throw new Error("Ze względów bezpieczeństwa zaloguj się ponownie przed usunięciem konta.");
    }
    throw new Error(e.message || "Błąd usuwania konta.");
  }
}
